import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../../lib/adminApi';
import { CalendarClock, Eye, Loader2, ArrowRight, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { DevelopmentSection } from '../../../components/tactical-editor/DevelopmentSection';
import { ScorecardSection } from '../../../components/tactical-editor/ScorecardSection';
import { FlywheelSection } from '../../../components/tactical-editor/FlywheelSection';
import { RiscSection } from '../../../components/tactical-editor/RiscSection';
import { LightningRoundSection } from '../../../components/tactical-editor/LightningRoundSection';
import { FacilityOverviewSection } from '../../../components/tactical-editor/FacilityOverviewSection';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const now = new Date();
const YEARS = [now.getFullYear(), now.getFullYear() + 1, now.getFullYear() + 2];

const noop = () => {};

export const MonthPreviewHubTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);

  const load = useCallback(async (y, m) => {
    setLoading(true);
    try {
      const res = await adminApi.tacticalPreview(y, m);
      setData(res);
      setYear(res.year);
      setMonth(res.month);
    } catch (err) {
      toast.error(err.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load resolves "next month" on the server.
  useEffect(() => { load(null, null); }, [load]);

  const t = data?.tactical;
  const sites = useMemo(() => t?.facility_sites || [], [t]);
  const activeSite = sites.length > 0 ? sites[0] : null;

  return (
    <div data-testid="month-preview-tab">
      <div className="flex items-start justify-between gap-6 mb-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-slate-500" />
            <h2 className="text-lg font-bold text-slate-900">Month Preview</h2>
          </div>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">
            The real tactical sheet, rendered exactly as everyone will see it on the 1st.
            Nothing is saved and no one else is affected.
          </p>
        </div>

        <div className="flex items-end gap-2">
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Month</label>
            <select
              value={month ?? ''}
              onChange={(e) => setMonth(Number(e.target.value))}
              data-testid="month-preview-month-select"
              className="px-2 py-1.5 rounded border border-slate-300 text-sm bg-white"
            >
              {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Year</label>
            <select
              value={year ?? ''}
              onChange={(e) => setYear(Number(e.target.value))}
              data-testid="month-preview-year-select"
              className="px-2 py-1.5 rounded border border-slate-300 text-sm bg-white"
            >
              {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button
            onClick={() => load(year, month)}
            disabled={loading}
            data-testid="month-preview-load-button"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
            Preview
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="text-sm text-slate-500" data-testid="month-preview-loading">Loading preview…</div>
      )}

      {data && (
        <>
          <div
            className="flex items-center gap-3 flex-wrap rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-4"
            data-testid="month-preview-banner"
          >
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-200/70 px-2 py-0.5 rounded">
              <Lock className="w-3 h-3" /> Read-only preview
            </span>
            <span className="text-sm text-amber-900" data-testid="month-preview-heading">
              <strong>{data.month_name} {data.year}</strong> tactical template
              {data.is_current_month ? ' (current month)' : ''}
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="month-preview-sections">
            {data.sections.map((s, i) => (
              <React.Fragment key={s}>
                {i > 0 && <ArrowRight className="w-3 h-3 text-slate-300" />}
                <span className="text-xs font-semibold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-full">{s}</span>
              </React.Fragment>
            ))}
          </div>

          <p className="text-xs text-slate-500 mb-4">
            {data.is_flywheel_era
              ? 'Scorecard is retired for this month — the Flywheel graphic takes its place.'
              : 'This month still uses the historical Red/Yellow/Green Scorecard.'}
            {data.is_risc_era ? ' R.I.S.C. is included and starts blank every month.' : ''}
          </p>

          {/*
            The genuine editor component tree inside the genuine `tactical-sheet`
            wrapper — and the same `max-w-5xl px-4` container the real page uses —
            so this is a true WYSIWYG preview rather than a summary.
            Inputs keep their normal (not greyed-out) styling for fidelity;
            `pointer-events-none` is what makes it inert, and every handler is a
            no-op with no autosave attached, so nothing can be written.
          */}
          <div className="max-w-5xl mx-auto px-4">
          <div
            inert={true}
            className="tactical-sheet rounded-lg overflow-hidden pointer-events-none select-none"
            data-testid="month-preview-sheet"
          >
            <DevelopmentSection development={t.development} onChange={noop} />

            {data.is_flywheel_era ? (
              <FlywheelSection />
            ) : (
              <div data-testid="month-preview-scorecard">
                <ScorecardSection scorecard={t.scorecard} onChange={noop} />
              </div>
            )}

            <LightningRoundSection lightningRound={t.lightning_round} onChange={noop} />

            {data.is_risc_era && <RiscSection risc={t.risc} onChange={noop} />}

            <div data-testid="month-preview-facility">
              <FacilityOverviewSection
                tacticalId={null}
                facilitySites={sites}
                facilityOverview={t.facility_overview}
                activeSiteId={activeSite?.id}
                activeSite={activeSite}
                hasTah={t.has_tah || false}
                tahLoading={false}
                isOwner
                onSiteChange={noop}
                onSitesUpdated={noop}
                onSiteItemChange={noop}
                onLegacyItemChange={noop}
                onTahToggle={noop}
              />
            </div>
          </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MonthPreviewHubTab;
